import { Button, Modal, Dropdown, message } from 'antd';
import { EditOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { usePosts, useChallenges, useLearningPaths } from '../../../hooks/useCommunity';
import communityAPI from '../../../api/community';

// CRUD Actions for Posts
export const PostActions = ({ post, onEdit, onDeleted }) => {
  const { user } = useCommunityAuth();
  const { deletePost } = usePosts({}, { skip: true });

  const isAdmin = user && user.role === 'admin';
  const isAuthor = user && user.id === post.author?.id;
  const reviewStatus = post?.review_status || 'pending_review';
  const isPendingApproval = reviewStatus !== 'approved';
  const requiresRevision = reviewStatus === 'requires_revision';

  if (!user || (!isAuthor && !isAdmin)) {
    return null;
  }

  const deletePrompt = requiresRevision
    ? 'Deleting this post will remove it from the review queue and discard the moderator feedback. Continue?'
    : isPendingApproval && isAuthor && !isAdmin
      ? 'Deleting will remove this post from the approval queue. Continue?'
      : `Are you sure you want to delete "${post.title}"?`;

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Post',
      content: deletePrompt,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deletePost(post.slug);
          message.success('Post deleted');
          onDeleted && onDeleted(post);
        } catch (error) {
          message.error('Failed to delete post');
          throw error;
        }
      }
    });
  };

  const handleApprove = async () => {
    try {
      await communityAPI.posts.approve(post.slug);
      message.success('Post approved successfully');
      window.location.reload();
    } catch (err) {
      message.error('Failed to approve post');
    }
  };

  const items = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: onEdit
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete
    }
  ];

  // Add approve option for admins on unapproved posts
  if (isAdmin && isPendingApproval) {
    items.unshift({
      key: 'approve',
      label: 'Approve',
      icon: <span>✓</span>,
      onClick: handleApprove
    });
  }

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button className='bg-white/70 text-slate-black' type="text" icon={<MoreOutlined />} />
    </Dropdown>
  );
};

// CRUD Actions for Challenges
export const ChallengeActions = ({ challenge, onEdit, onDeleted }) => {
  const { user } = useCommunityAuth();
  const { deleteChallenge } = useChallenges({}, { skip: true });

  if (!user || (user.id !== challenge.author?.id && user.role !== 'admin')) {
    return null;
  }

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Challenge',
      content: `Are you sure you want to delete "${challenge.title}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await deleteChallenge(challenge.slug);
        onDeleted && onDeleted(challenge);
      }
    });
  };

  const items = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: onEdit
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete
    }
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button type="text" icon={<MoreOutlined />} />
    </Dropdown>
  );
};

// CRUD Actions for Learning Paths
export const LearningPathActions = ({ learningPath, onEdit, onDeleted }) => {
  const { user } = useCommunityAuth();
  const { deleteLearningPath } = useLearningPaths({}, { skip: true });

  if (!user || (user.id !== learningPath.author?.id && user.role !== 'admin')) {
    return null;
  }

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Learning Path',
      content: `Are you sure you want to delete "${learningPath.title}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await deleteLearningPath(learningPath.id);
        onDeleted && onDeleted(learningPath);
      }
    });
  };

  const items = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: onEdit
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete
    }
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button type="text" icon={<MoreOutlined />} />
    </Dropdown>
  );
};

// Generic resource actions for reuse
export const ResourceActions = ({
  resource,
  canEdit,
  onEdit,
  onDelete,
  typeLabel = 'Item',
  onDeleted
}) => {
  if (!canEdit) return null;

  const handleDelete = () => {
    Modal.confirm({
      title: `Delete ${typeLabel}`,
      content: `Are you sure you want to delete "${resource.title || resource.name || typeLabel}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await onDelete(resource);
        onDeleted && onDeleted(resource);
      }
    });
  };

  const items = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: onEdit
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete
    }
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button type="text" icon={<MoreOutlined />} />
    </Dropdown>
  );
};